import React, { createContext, useContext, useEffect, useMemo, useState, useCallback } from 'react';
import {
  collection,
  onSnapshot,
  addDoc,
  updateDoc,
  deleteDoc,
  doc,
  query,
  where
} from 'firebase/firestore';
import { db } from '../firebase/config';
import { useAuth } from './AuthContext';
import toast from 'react-hot-toast';

const TestimonialContext = createContext();

export const useTestimonial = () => {
  const context = useContext(TestimonialContext);
  if (!context) {
    throw new Error('useTestimonial must be used within a TestimonialProvider');
  }
  return context;
};

const FALLBACK_TESTIMONIALS = [
  {
    id: 'fallback-1',
    name: 'Aissatou Diallo',
    role: 'Habitante · Kaloum',
    content:
      'Je trouve les bons restos et les adresses utiles en quelques secondes. C’est devenu mon réflexe dès que je sors à Conakry.',
    rating: 5,
    status: 'approved',
    isFallback: true
  },
  {
    id: 'fallback-2',
    name: 'Mohamed Camara',
    role: 'Commerçant',
    content:
      'Avec l’encaissement QR et le tableau de bord, mes clients paient plus facilement et je suis mieux visible dans mon quartier.',
    rating: 5,
    status: 'approved',
    isFallback: true
  },
  {
    id: 'fallback-3',
    name: 'Fatoumata Bah',
    role: 'Voyageuse',
    content:
      'Taxi, hôtels, billetterie : tout est au même endroit. Ça m’a vraiment simplifié mon séjour à Conakry.',
    rating: 5,
    status: 'approved',
    isFallback: true
  },
  {
    id: 'fallback-4',
    name: 'Ibrahima Sano',
    role: 'Habitant · Dixinn',
    content:
      'Le portefeuille et les infos trafic m’évitent beaucoup de stress au quotidien. Simple, clair, utile.',
    rating: 5,
    status: 'approved',
    isFallback: true
  }
];

const sortByCreatedAtDesc = (rows) =>
  [...rows].sort((a, b) => String(b.createdAt || '').localeCompare(String(a.createdAt || '')));

export const TestimonialProvider = ({ children }) => {
  const { currentUser, isAdmin } = useAuth();
  const admin = Boolean(isAdmin?.());
  const [testimonials, setTestimonials] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const q = admin
      ? query(collection(db, 'homepageTestimonials'))
      : query(collection(db, 'homepageTestimonials'), where('status', '==', 'approved'));

    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        const rows = sortByCreatedAtDesc(
          snapshot.docs.map((item) => ({
            id: item.id,
            ...item.data()
          }))
        );
        setTestimonials(rows);
        setLoading(false);
      },
      (error) => {
        console.error('Erreur chargement témoignages:', error);
        setTestimonials([]);
        setLoading(false);
      }
    );
    return unsubscribe;
  }, [admin]);

  const submitTestimonial = async ({ name, role, content, rating }) => {
    const cleanName = (name || '').trim();
    const cleanContent = (content || '').trim();
    const cleanRole = (role || '').trim();
    const stars = Math.min(5, Math.max(1, Number(rating) || 5));

    if (cleanName.length < 2) {
      toast.error('Indiquez votre nom.');
      return null;
    }
    if (cleanContent.length < 20) {
      toast.error('Le témoignage doit contenir au moins 20 caractères.');
      return null;
    }

    try {
      const payload = {
        name: cleanName,
        role: cleanRole || 'Utilisateur',
        content: cleanContent,
        rating: stars,
        status: 'pending',
        userId: currentUser?.uid || null,
        userEmail: currentUser?.email || '',
        createdAt: new Date().toISOString()
      };

      const refDoc = await addDoc(collection(db, 'homepageTestimonials'), payload);
      toast.success('Merci ! Votre témoignage sera visible après validation.');
      return { id: refDoc.id, ...payload };
    } catch (error) {
      console.error('Erreur soumission témoignage:', error);
      toast.error('Impossible d’envoyer le témoignage.');
      return null;
    }
  };

  const moderateTestimonial = async (testimonialId, status) => {
    if (!['approved', 'rejected', 'pending'].includes(status)) return false;
    try {
      await updateDoc(doc(db, 'homepageTestimonials', testimonialId), {
        status,
        moderatedAt: new Date().toISOString(),
        moderatedBy: currentUser?.uid || null
      });
      toast.success(
        status === 'approved'
          ? 'Témoignage publié.'
          : status === 'rejected'
            ? 'Témoignage rejeté.'
            : 'Statut mis à jour.'
      );
      return true;
    } catch (error) {
      console.error('Erreur modération témoignage:', error);
      toast.error('Impossible de modérer ce témoignage.');
      return false;
    }
  };

  const deleteTestimonial = async (testimonialId) => {
    try {
      await deleteDoc(doc(db, 'homepageTestimonials', testimonialId));
      toast.success('Témoignage supprimé.');
      return true;
    } catch (error) {
      console.error('Erreur suppression témoignage:', error);
      toast.error('Impossible de supprimer le témoignage.');
      return false;
    }
  };

  const getApprovedTestimonials = useCallback(() => {
    const approved = testimonials.filter((item) => item.status === 'approved');
    return approved.length > 0 ? approved : FALLBACK_TESTIMONIALS;
  }, [testimonials]);

  const getPendingTestimonials = useCallback(
    () => testimonials.filter((item) => item.status === 'pending'),
    [testimonials]
  );

  const value = useMemo(
    () => ({
      testimonials,
      loading,
      submitTestimonial,
      moderateTestimonial,
      deleteTestimonial,
      getApprovedTestimonials,
      getPendingTestimonials
    }),
    [testimonials, loading, getApprovedTestimonials, getPendingTestimonials, currentUser]
  );

  return (
    <TestimonialContext.Provider value={value}>{children}</TestimonialContext.Provider>
  );
};
